import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import BackToTop from "@/components/BackToTop";
import AddEmailPrompt from "@/components/AddEmailPrompt";
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";
import PromoBanner from "@/components/PromoBanner";

// Code-split every non-critical page so they load on demand instead of bloating the homepage bundle.
const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const Orders = lazy(() => import("@/pages/Orders"));
const Admin = lazy(() => import("@/pages/Admin"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const SizeGuide = lazy(() => import("@/pages/SizeGuide"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-10 h-10 rounded-full border-4 border-pink-200 border-t-pink-500 animate-spin" aria-label="Loading page"/>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  const firstLoad = React.useRef(true);
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // Meta Pixel: base code already fires PageView on initial load;
    // fire it again on subsequent SPA route changes.
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [pathname, search]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback/>;
  return (
    <>
      <ScrollToTop/>
      <PromoBanner/>
      <Navbar/>
      <CartDrawer/>
      <main className="min-h-screen">
        <Suspense fallback={<PageLoader/>}>
          <Routes>
            <Route path="/" element={<Home/>}/>
            <Route path="/shop" element={<Shop/>}/>
            <Route path="/product/:id" element={<ProductDetail/>}/>
            <Route path="/checkout" element={<Checkout/>}/>
            <Route path="/login" element={<Login/>}/>
            <Route path="/register" element={<Register/>}/>
            <Route path="/account" element={<Account/>}/>
            <Route path="/orders" element={<Orders/>}/>
            <Route path="/orders/:id" element={<Orders/>}/>
            <Route path="/admin" element={<Admin/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/reset-password" element={<ResetPassword/>}/>
            <Route path="/track-order" element={<TrackOrder/>}/>
            <Route path="/faq" element={<FAQ/>}/>
            <Route path="/about" element={<About/>}/>
            <Route path="/contact" element={<Contact/>}/>
            <Route path="/size-guide" element={<SizeGuide/>}/>
            <Route path="/privacy" element={<LegalPage section="privacy"/>}/>
            <Route path="/terms" element={<LegalPage section="terms"/>}/>
            <Route path="/shipping-policy" element={<LegalPage section="shipping"/>}/>
            <Route path="/wishlist" element={<Wishlist/>}/>
            <Route path="/admin-login" element={<AdminLogin/>}/>
            <Route path="/auth-callback" element={<AuthCallback/>}/>
            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </Suspense>
      </main>
      <Footer/>
      <WhatsAppFloat/>
      <BackToTop/>
      <AddEmailPrompt/>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <AppRouter/>
                <Toaster position="top-right" richColors closeButton/>
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
